import React from "react";
import { CreditCard, Banknote, Wallet, CalendarClock, Sparkles } from "lucide-react";
import { fmtEur, Row } from "./shared";

const PaymentOption = ({ value, current, onClick, icon: Icon, title, desc, disabled, disabledNote }) => (
  <button type="button" onClick={() => !disabled && onClick(value)} disabled={disabled}
    className={`w-full text-left p-4 rounded-2xl border-2 transition-colors flex items-start gap-3 ${
      disabled ? "border-stone-100 bg-stone-50 opacity-50 cursor-not-allowed" :
      value === current ? "border-deep-rose bg-blush-light" : "border-stone-200 bg-white hover:border-blush"
    }`}
    data-testid={`payment-option-${value}`}>
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${value === current ? "bg-deep-rose text-white" : "bg-blush-light text-deep-rose"}`}>
      <Icon size={20} />
    </div>
    <div className="min-w-0 flex-1">
      <div className="font-serif-display text-stone-800">{title}</div>
      <div className="text-stone-500 text-xs mt-0.5">{desc}</div>
      {disabledNote && <div className="text-amber-700 text-xs mt-1">{disabledNote}</div>}
    </div>
  </button>
);

const Step3Payment = ({ paymentMode, setPaymentMode, message, setMessage, quote, quoteLoading, settings, stripeEnabled, consents, setConsents }) => {
  const chequeDesc = settings?.chequeNote || "À l'ordre de L'Élan Danse. Possibilité de chèques échelonnés jusqu'à 3, à remettre impérativement au forum en début d'année.";
  const especesDesc = settings?.especesNote || "Le règlement en espèces se fait exclusivement lors du forum d'inscription (pas par courrier).";
  const cgvUrl = settings?.legalCgvUrl || "/legal/cgv.pdf";
  const reglementUrl = settings?.legalReglementUrl || "/legal/reglement-interieur.pdf";
  const imageUrl = settings?.legalImageUrl || "/legal/annexe-rgpd.pdf";
  const toggle = (k) => (e) => setConsents({ ...(consents || {}), [k]: e.target.checked });
  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-serif-display text-2xl text-stone-800">3. Récapitulatif &amp; paiement</h3>
        <p className="text-stone-500 text-sm mt-1">Vérifiez les inscriptions et choisissez votre mode de paiement.</p>
      </div>

      <div className="bg-cream rounded-2xl p-5">
        <h4 className="font-serif-display text-lg text-stone-800 mb-3">Détail</h4>
        {quoteLoading ? <div className="text-stone-500 text-sm">Calcul du tarif...</div> : quote ? (
          <div className="space-y-3 text-sm">
            {(quote.lines || []).map((l, i) => (
              <div key={i} className="bg-white rounded-xl p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-serif-display text-stone-800">{l.studentFirstName}</div>
                    {l.tierLabel ? (
                      <div className="text-stone-500 text-xs">{l.tierLabel} · {l.slotsCount} créneau{l.slotsCount > 1 ? "x" : ""}</div>
                    ) : (
                      <div className="text-stone-500 text-xs">{l.slotsCount} créneau{l.slotsCount > 1 ? "x" : ""}</div>
                    )}
                    {l.discountPercent > 0 && <div className="text-emerald-600 text-xs mt-1">Remise sur le créneau le moins cher −{l.discountPercent}% (−{fmtEur(l.discountAmount)})</div>}
                  </div>
                  <div className="text-right shrink-0">
                    {l.discountPercent > 0 && <div className="text-stone-400 text-xs line-through">{fmtEur(l.baseAmount)}</div>}
                    <div className="text-stone-800 font-serif-display text-lg">{fmtEur(l.finalAmount)}</div>
                  </div>
                </div>
                {(l.slots || []).length > 0 && (
                  <ul className="mt-2 pt-2 border-t border-stone-100 text-xs text-stone-600 space-y-0.5">
                    {l.slots.map((s, j) => (
                      <li key={j} className={s.isDiscountSlot ? "text-emerald-700 flex justify-between gap-2" : "flex justify-between gap-2"}>
                        <span>· {s.title} <span className="text-stone-400">({s.startTime}-{s.endTime})</span>
                          {s.isDiscountSlot && <span className="ml-1 text-[10px] uppercase tracking-widest text-emerald-700">— créneau remisé</span>}
                        </span>
                        {s.price > 0 && <span className="text-stone-500 shrink-0">{fmtEur(s.price)}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
            <div className="border-t border-stone-200 pt-3 mt-3 space-y-1">
              <Row label="Sous-total créneaux" value={fmtEur(quote.subtotal)} />
              {quote.discountAmount > 0 && <Row label={`Remise multi-cours (par élève)`} value={`- ${fmtEur(quote.discountAmount)}`} color="text-emerald-600" />}
              <Row
                label={
                  `Cotisation association${quote.cotisationIsLocal ? " (résident " + (quote.cotisationPostalCode || "local") + ")" : " (hors commune)"}` +
                  (quote.cotisationStudents > 1 ? ` (${quote.cotisationStudents} × ${fmtEur(quote.cotisationPerStudent)})` : "")
                }
                value={fmtEur(quote.cotisation)}
              />
              {quote.licenseFFDTotal > 0 && (
                <Row
                  label={`Licence FFD${quote.licenseFFDStudents > 1 ? ` (${quote.licenseFFDStudents} × ${fmtEur(quote.licenseFFDPerStudent)})` : ""}`}
                  value={fmtEur(quote.licenseFFDTotal)}
                />
              )}
              {quote.applicationFee > 0 && (
                <Row label={quote.applicationFeeLabel || "Frais de traitement"} value={fmtEur(quote.applicationFee)} />
              )}
              <div className="flex items-center justify-between pt-2 mt-2 border-t border-stone-200">
                <span className="font-serif-display text-stone-800">Total à régler</span>
                <span className="font-serif-display text-2xl text-deep-rose" data-testid="quote-total">{fmtEur(quote.total)}</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-stone-500 text-sm">Aucun créneau sélectionné.</div>
        )}
      </div>

      <div>
        <h4 className="font-serif-display text-lg text-stone-800 mb-3">Mode de paiement</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <PaymentOption value="cheque" current={paymentMode} onClick={setPaymentMode} icon={Banknote} title="Chèque" desc={chequeDesc} />
          <PaymentOption value="especes" current={paymentMode} onClick={setPaymentMode} icon={Wallet} title="Espèces" desc={especesDesc} />
          <PaymentOption value="card" current={paymentMode} onClick={setPaymentMode} icon={CreditCard}
            title="Carte bancaire (en ligne)" desc="Paiement sécurisé Stripe — intégralité du montant."
            disabled={!stripeEnabled} disabledNote={!stripeEnabled ? "Indisponible pour le moment" : ""} />
          <PaymentOption value="monthly" current={paymentMode} onClick={setPaymentMode} icon={CalendarClock}
            title="Prélèvement SEPA mensuel (8x)" desc="Prélèvement automatique sur votre compte bancaire (IBAN) chaque mois pendant 8 mois. Vous signez le mandat en ligne — plus rien à faire ensuite."
            disabled={!stripeEnabled} disabledNote={!stripeEnabled ? "Indisponible pour le moment" : ""} />
        </div>
        {paymentMode === "monthly" && quote && (
          <p className="text-stone-500 text-xs mt-3 italic">
            Soit <strong>{fmtEur(quote.total / 8)}/mois</strong> sur 8 mensualités. Le mandat SEPA est signé une seule fois en ligne&nbsp;; les 8 prélèvements sont ensuite effectués automatiquement par Stripe à partir du <strong>{settings?.sepaFirstDebitDate ? new Date(settings.sepaFirstDebitDate).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : "1er octobre"}</strong>. Aucun renouvellement à valider chaque mois.
          </p>
        )}
      </div>

      {settings?.passPlusEnabled && settings?.passPlusNote && (
        <div className="bg-cream-yellow/60 border-l-4 border-amber-600 rounded-2xl p-5" data-testid="pass-plus-info">
          <div className="flex items-start gap-3">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-amber-600 text-white shrink-0 mt-0.5">
              <Sparkles size={18} />
            </span>
            <div className="min-w-0 flex-1">
              <h4 className="font-serif-display text-lg text-stone-800">{settings.passPlusTitle || "Note pour le Pass+"}</h4>
              <div className="text-stone-700 text-sm mt-2 whitespace-pre-line leading-relaxed">{settings.passPlusNote}</div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-blush-light p-5" data-testid="consents-block">
        <h4 className="font-serif-display text-lg text-stone-800">Avant de finaliser</h4>
        <p className="text-stone-500 text-sm mt-1">Merci de prendre connaissance des documents suivants et de cocher les cases correspondantes.</p>

        <div className="mt-4 space-y-3">
          <div className="text-xs uppercase tracking-widest text-deep-rose font-medium">Obligatoire</div>

          <label className="flex items-start gap-3 cursor-pointer" data-testid="consent-cgv-label">
            <input type="checkbox" checked={!!consents?.cgv} onChange={toggle("cgv")} className="mt-1 h-4 w-4 accent-deep-rose shrink-0" data-testid="consent-cgv" />
            <span className="text-sm text-stone-700">
              J&apos;ai lu et j&apos;accepte les <a href={cgvUrl} target="_blank" rel="noopener noreferrer" className="text-deep-rose underline">conditions générales de vente</a>. <span className="text-deep-rose font-medium">*</span>
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer" data-testid="consent-reglement-label">
            <input type="checkbox" checked={!!consents?.reglement} onChange={toggle("reglement")} className="mt-1 h-4 w-4 accent-deep-rose shrink-0" data-testid="consent-reglement" />
            <span className="text-sm text-stone-700">
              J&apos;ai lu et j&apos;accepte le <a href={reglementUrl} target="_blank" rel="noopener noreferrer" className="text-deep-rose underline">règlement intérieur</a> de l&apos;école. <span className="text-deep-rose font-medium">*</span>
            </span>
          </label>

          <div className="text-xs uppercase tracking-widest text-stone-500 font-medium pt-3 border-t border-stone-100 mt-4">Optionnel</div>

          <label className="flex items-start gap-3 cursor-pointer bg-blush-light/30 rounded-lg p-3 -mx-1" data-testid="consent-image-label">
            <input type="checkbox" checked={!!consents?.image} onChange={toggle("image")} className="mt-1 h-4 w-4 accent-deep-rose shrink-0" data-testid="consent-image" />
            <span className="text-sm text-stone-700">
              J&apos;autorise que mon enfant soit photographié et/ou filmé dans le cadre des activités, et que ces images soient utilisées à des fins de communication (site internet, réseaux sociaux, supports pédagogiques). Voir le <a href={imageUrl} target="_blank" rel="noopener noreferrer" className="text-deep-rose underline">détail du droit à l&apos;image</a>. <span className="text-stone-500 italic text-xs">(facultatif)</span>
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer bg-blush-light/30 rounded-lg p-3 -mx-1" data-testid="consent-whatsapp-label">
            <input type="checkbox" checked={!!consents?.whatsapp} onChange={toggle("whatsapp")} className="mt-1 h-4 w-4 accent-deep-rose shrink-0" data-testid="consent-whatsapp" />
            <span className="text-sm text-stone-700">
              J&apos;accepte que mon enfant / mes coordonnées figurent dans le groupe WhatsApp utilisé pour les échanges liés aux activités. <span className="text-stone-500 italic text-xs">(facultatif)</span>
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer bg-blush-light/30 rounded-lg p-3 -mx-1" data-testid="consent-cse-label">
            <input type="checkbox" checked={!!consents?.cseAttestation} onChange={toggle("cseAttestation")} className="mt-1 h-4 w-4 accent-deep-rose shrink-0" data-testid="consent-cse" />
            <span className="text-sm text-stone-700">
              J&apos;ai besoin d&apos;une <strong>attestation CSE</strong> (à fournir à mon comité d&apos;entreprise pour remboursement partiel). L&apos;école vous la transmettra par email après validation de l&apos;inscription. <span className="text-stone-500 italic text-xs">(facultatif)</span>
            </span>
          </label>
        </div>

        <p className="text-xs text-stone-500 mt-4 pt-3 border-t border-stone-100 leading-relaxed" data-testid="inscription-privacy-note">
          Les informations recueillies sont utilisées uniquement pour la gestion de votre inscription, la facturation et
          l&apos;information des adhérents. Elles ne sont jamais revendues. Vous disposez d&apos;un droit d&apos;accès, de
          rectification et de suppression : consultez notre{" "}
          <a href="/politique-de-confidentialite" target="_blank" rel="noopener noreferrer" className="text-deep-rose underline">politique de confidentialité</a>.
        </p>
      </div>

      <div>
        <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1.5">Message (optionnel)</label>
        <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} className="w-full bg-cream rounded-xl border border-blush-light px-4 py-3 text-sm" placeholder="Une demande particulière ?" />
      </div>
    </div>
  );
};

export default Step3Payment;
